/**
 * O parágrafo de status que VIVE COM A PÁGINA, e não com a aba: o papel do `#status-milhoes`,
 * `#status-coloracao`, `#status-lista-desejos` e `#status-avaliativo` do legado
 * (`admin.html:2643`, `2727`, `2744`, `2763`).
 *
 * POR QUE NÃO O `useStatusTemporario` DA PEÇA COMUM: ele guarda o texto no estado do componente, e
 * a moldura da ficha só desenha a aba ativa (`ficha-cliente/index.tsx:414-416`). Trocar de aba
 * desmonta a aba e o texto volta vazio; a resposta de uma gravação que chega depois cai num
 * componente que não existe mais, e a pessoa fica sem saber se gravou. No legado o parágrafo é um
 * nó do DOM que nunca sai da página: só quem escreve nele muda o texto, a troca de aba não, e a
 * resposta do `upsert` escreve nele esteja a aba à vista ou não (`5145`, `5335`, `5387`, `5415`).
 * Achado da verificação independente da A7 (`progresso/VERIFICACAO-A7.md`, linhas 199, 206, 207 e
 * 210 do inventário).
 *
 * Um parágrafo por aba, cada um criado UMA vez no módulo da aba: o módulo vive enquanto a página
 * vive, como o nó do legado. Não há um por cliente, porque o legado também não tem: o texto de uma
 * cliente continua lá ao abrir outra, até alguém escrever por cima.
 *
 * POR QUE MORA AQUI: o território da frente A7 são as quatro abas e as subpastas delas. O lugar
 * natural seria `apps/admin/src/comum/`, ao lado do `useStatusTemporario`, e a mudança foi pedida
 * ao principal.
 */

// O gancho do React que assina uma fonte externa ao componente e redesenha quando ela muda.
import { useSyncExternalStore } from 'react';

/** O parágrafo de uma aba: ler, escrever, escrever com prazo e avisar quem está desenhando. */
export interface StatusDaPagina {
  /** O texto atual, o `textContent` do parágrafo. */
  readonly ler: () => string;
  /** Escreve e fica, como `status.textContent = texto` do legado. */
  readonly escrever: (texto: string) => void;
  /**
   * Escreve e apaga depois do prazo, MAS só se o texto ainda for o mesmo: o
   * `setTimeout(() => { if (status.textContent === 'Salvo!') status.textContent = ''; }, 3000)`
   * do legado (`5146`, `5336`, `5388`, `5416`). Uma escrita no meio do caminho não é apagada.
   */
  readonly mostrar: (texto: string, prazoMs?: number) => void;
  /** Quem desenha o parágrafo se inscreve aqui; devolve a função que cancela a inscrição. */
  readonly assinar: (aoMudar: () => void) => () => void;
}

/** Cria um parágrafo vazio, como o `<p ... role="status"></p>` do HTML do legado. */
export function criarStatusDaPagina(): StatusDaPagina {
  // O texto do parágrafo, fora de qualquer componente: sobrevive à aba desmontar.
  let texto = '';
  // Quem está desenhando o parágrafo agora (zero ou uma aba, já que só a ativa é desenhada).
  const ouvintes = new Set<() => void>();
  // Troca o texto e avisa quem desenha; escrever o mesmo texto não redesenha nada.
  const escrever = (novo: string) => {
    if (novo === texto) return;
    texto = novo;
    for (const aoMudar of ouvintes) aoMudar();
  };
  return {
    ler: () => texto,
    escrever,
    // 3000 ms: o prazo do "Salvo!" nas quatro abas do legado.
    mostrar: (novo, prazoMs = 3000) => {
      escrever(novo);
      // O relógio mora fora do componente, como o `setTimeout` do legado, e corre mesmo com a aba
      // fora da tela; ao disparar, só apaga se ninguém escreveu outra coisa no meio.
      window.setTimeout(() => {
        if (texto === novo) escrever('');
      }, prazoMs);
    },
    assinar: (aoMudar) => {
      ouvintes.add(aoMudar);
      return () => {
        ouvintes.delete(aoMudar);
      };
    },
  };
}

/** O texto atual do parágrafo, e a aba redesenha a cada escrita nele. */
export function useStatusDaPagina(status: StatusDaPagina): string {
  // O segundo `ler` é o valor para o desenho no servidor, que este app não faz, mas o gancho pede.
  return useSyncExternalStore(status.assinar, status.ler, status.ler);
}
